// Hover preview of a sheet (Larry 2026-08-20): rasterizes page 1 of the PDF
// behind a presigned URL with pdf.js and floats it in a fixed card beside the
// hovered element. Shared by the Drawings/Specs lists and the viewer's
// "Open a document" picker. Renders cache per URL for the page's life, so a
// second hover is instant.
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"

const CACHE = new Map()
const MAX_CACHE = 40
const WIDTH = 460
let card = null
let token = 0

// The list controllers set the pdf.js worker from their own Stimulus value;
// when the first hover happens elsewhere (the viewer's Open picker), borrow
// the worker URL from whichever host element carries it.
function workerReady() {
  if (pdfjsLib.GlobalWorkerOptions.workerSrc) return true
  // data-pdf-worker-url is the generic host attribute (the ChatLog panel
  // carries it on every project page); the two list-page Stimulus values
  // remain for pages that predate it.
  const host = document.querySelector("[data-pdf-worker-url], [data-drawings-specs-pdf-worker-url-value], [data-specifications-pdf-worker-url-value]")
  const url = host && (host.dataset.pdfWorkerUrl || host.dataset.drawingsSpecsPdfWorkerUrlValue || host.dataset.specificationsPdfWorkerUrlValue)
  if (url) pdfjsLib.GlobalWorkerOptions.workerSrc = url
  return !!pdfjsLib.GlobalWorkerOptions.workerSrc
}

function rasterize(url) {
  if (CACHE.has(url)) return CACHE.get(url)
  const p = (async () => {
    const buf = await (await fetch(url)).arrayBuffer()
    const doc = await pdfjsLib.getDocument({ data: buf }).promise
    try {
      const page = await doc.getPage(1)
      const base = page.getViewport({ scale: 1 })
      // Render at 2x the display width so plan linework stays crisp.
      const vp = page.getViewport({ scale: (WIDTH * 2) / base.width })
      const canvas = document.createElement("canvas")
      canvas.width = Math.ceil(vp.width)
      canvas.height = Math.ceil(vp.height)
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise
      // Display dims ride along so the card can size itself BEFORE the
      // image decodes — measuring after innerHTML sees a zero-height img
      // and the card then grows past the bottom edge (Phelipe 2026-08-21).
      return { dataUrl: canvas.toDataURL("image/jpeg", 0.82), w: Math.ceil(vp.width / 2), h: Math.ceil(vp.height / 2) }
    } finally {
      doc.destroy?.()
    }
  })()
  CACHE.set(url, p)
  p.catch(() => CACHE.delete(url))
  if (CACHE.size > MAX_CACHE) CACHE.delete(CACHE.keys().next().value)
  return p
}

// S2-14: when the server already made the sheet's page-1 picture, show that
// — no PDF download, no rasterizing, instant on Firefox too. Same shape as
// rasterize()'s result, sized to the card width.
function fromImage(url) {
  if (CACHE.has(url)) return CACHE.get(url)
  const p = new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const w = WIDTH
      const h = Math.max(1, Math.round((img.naturalHeight * WIDTH) / Math.max(1, img.naturalWidth)))
      resolve({ dataUrl: url, w, h })
    }
    img.onerror = () => reject(new Error("sheet picture failed to load"))
    img.src = url
  })
  CACHE.set(url, p)
  p.catch(() => CACHE.delete(url))
  if (CACHE.size > MAX_CACHE) CACHE.delete(CACHE.keys().next().value)
  return p
}

function ensureCard() {
  if (card && card.isConnected) return card
  card = document.createElement("div")
  // pointer-events:none — the card can never trap the mouse, so mouseleave on
  // the anchor is the single source of truth for hiding it. Max z-index: the
  // viewer's Open picker sits at 2147483646 (above Apryse) and this floats
  // above that.
  card.style.cssText = "position:fixed;z-index:2147483647;pointer-events:none;background:#fff;" +
    "border:1px solid #e5e7eb;border-radius:10px;box-shadow:0 12px 32px rgba(0,0,0,0.18);padding:6px;"
  document.body.appendChild(card)
  return card
}

function position(anchor) {
  if (!card || !anchor?.isConnected) return
  const r = anchor.getBoundingClientRect()
  const w = card.offsetWidth
  const h = card.offsetHeight
  let left = r.right + 12
  if (left + w > window.innerWidth - 8) left = r.left - w - 12
  if (left < 8) left = 8
  const top = Math.min(Math.max(r.top + r.height / 2 - h / 2, 8), Math.max(window.innerHeight - h - 8, 8))
  card.style.left = `${left}px`
  card.style.top = `${top}px`
}

export function showSheetPreview(anchor, url, label = "", imageUrl = "") {
  if (!url && !imageUrl) return
  if (!imageUrl && !workerReady()) return
  const my = ++token
  ensureCard()
  const labelBar = label
    ? `<div style="max-width:${WIDTH}px;padding:5px 4px 1px;font-size:12px;color:#6b7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${label.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}</div>`
    : ""
  card.innerHTML = `<div style="width:230px;height:150px;display:flex;align-items:center;justify-content:center;">` +
    `<svg style="width:22px;height:22px;color:#9ca3af;animation:spin 1s linear infinite;" fill="none" viewBox="0 0 24 24"><circle style="opacity:.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path style="opacity:.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg></div>` + labelBar
  position(anchor)
  // Any scroll shifts the anchor out from under the card — just drop it.
  window.addEventListener("scroll", hideSheetPreview, { once: true, capture: true })
  const source = imageUrl
    ? fromImage(imageUrl).catch(() => (url && workerReady()) ? rasterize(url) : Promise.reject(new Error("no preview")))
    : rasterize(url)
  source.then(res => {
    if (my !== token || !card) return
    // Explicit dims, capped to the window — the card never outgrows the
    // screen, and position() measures the real height immediately.
    let w = res.w
    let h = res.h
    const maxH = Math.max(window.innerHeight - 32, 120)
    if (h > maxH) { w = Math.round((w * maxH) / h); h = maxH }
    card.innerHTML = `<img src="${res.dataUrl}" style="display:block;width:${w}px;height:${h}px;border-radius:6px;" alt="">` + labelBar
    position(anchor)
    // Belt and braces: re-clamp once the image is actually painted.
    card.querySelector("img")?.decode?.().then(() => position(anchor)).catch(() => {})
  }).catch(() => {
    if (my !== token || !card) return
    card.innerHTML = `<div style="padding:18px 22px;font-size:12px;color:#9ca3af;">Preview unavailable</div>`
    position(anchor)
  })
}

export function hideSheetPreview() {
  token++
  if (card) {
    card.remove()
    card = null
  }
}
